import { ArrowLeftOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { Button, Card, Descriptions, Table } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { capitalize } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../AppContext';
import {
  LIMIT,
  LOGS_ACTION,
  ROUTES,
  TOPIC_ID,
  defaultLogTimeFormat,
} from '../../../common/constants';
import useRouter from '../../../hooks/useRouter';
import '../dashboard.less';
import { GET_HISTORY } from '../graphql/Queries';

dayjs.extend(utc);

export default function History() {
  const {
    setProjectTopicId,
    state: { topicsId },
  } = useContext(AppContext);
  const {
    navigate,
    location: { state },
  } = useRouter();

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(LIMIT);
  const [filters, setFilters] = useState({
    skip: 0,
    limit: LIMIT,
  });

  const [getHistory, { data, loading }] = useLazyQuery(GET_HISTORY, {
    fetchPolicy: 'network-only',
    onError() {},
  });
  useEffect(() => {
    getHistory({
      variables: {
        data: {
          // eslint-disable-next-line no-undef
          projectTopicId: topicsId ?? localStorage?.getItem(TOPIC_ID),
          ...filters,
        },
      },
    });
  }, [topicsId, filters]);

  const columns = [
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value) =>
        dayjs?.utc(value)?.local()?.format(defaultLogTimeFormat),
    },
    {
      title: 'User',
      dataIndex: 'user',
      key: 'user',
      render: (_, record) => (
        <>
          <p className="m-0">
            {record?.firstName ?? '-'} {record?.lastName ?? '-'}
          </p>
          <span>[{record?.email ?? '-'}]</span>
        </>
      ),
    },
    {
      title: 'Entity',
      dataIndex: 'entity',
      key: 'entity',
    },
    {
      title: 'Operation',
      dataIndex: 'action',
      key: 'action',
    },
  ];

  const onChange = (pagination) => {
    setCurrentPage(pagination?.current);
    setPageSize(pagination?.pageSize);
    setFilters({
      ...filters,
      skip: currentPage ? (pagination?.current - 1) * pagination?.pageSize : 0,
      limit: currentPage ? pagination?.pageSize : 50,
    });
  };

  const getRecords = (action, record, recordType) => {
    const recordData = record?.[recordType];
    let displayText;

    if (!recordData?.length) {
      return <span>No data available</span>;
    }

    const renderEnvironment = (environments) =>
      environments?.map((env) => env?.environment)?.join(', ');

    switch (action) {
      case LOGS_ACTION.ADDED:
        if (record?.entity === 'Environment') {
          displayText = recordData?.join(', ');
        } else if (recordData?.length === 1) {
          const item = recordData[0];
          displayText = `${item?.keyName} key added for ${
            item?.environment?.length > 1 ? "ENV's" : 'ENV'
          }: ${renderEnvironment(item?.environment)}`;
        } else {
          displayText = recordData
            ?.map(
              (item) =>
                `${item?.keyName} key added for ${
                  item?.environment?.length > 1 ? "ENV's" : 'ENV'
                }: ${renderEnvironment(item?.environment)}`,
            )
            ?.join(', <br/>');
        }
        // eslint-disable-next-line react/no-danger
        return <div dangerouslySetInnerHTML={{ __html: displayText }} />;

      case LOGS_ACTION.UPDATED:
        if (record?.entity === 'Key') {
          displayText = `${recordData?.[0]?.keyName?.[0]} is ${capitalize(
            recordData?.[0]?.type,
          )}`;
        } else {
          displayText = `
          <p class="m-0">ENV : ${recordData?.[0]?.environment ?? '-'}</p>
          <span>
            ${recordData?.[0]?.keyName || '-'} : ${
            recordData?.[0]?.value || '-'
          }
          </span>
        `;
        }

        // eslint-disable-next-line react/no-danger
        return <div dangerouslySetInnerHTML={{ __html: displayText }} />;

      case LOGS_ACTION.DELETED:
        return (
          <span>
            {record?.entity !== 'Environment'
              ? `${
                  recordData?.[0]?.keyName
                } key deleted from ENV: ${renderEnvironment(
                  recordData?.[0]?.environment,
                )}`
              : recordData.join(', ')}
          </span>
        );

      case LOGS_ACTION.DUPLICATED:
        return (
          <>
            {recordData?.map((env) => (
              <>
                <p className="m-0">ENV : {env?.environment ?? '-'}</p>
                <span>
                  {env?.keyName} : {env?.value === '' ? '---' : env?.value}
                </span>
              </>
            ))}
          </>
        );

      case LOGS_ACTION.VERIFIED:
        return (
          <span>
            Verified: {recordData?.[0]?.isVerified === 'true' ? 'Yes' : 'No'}
          </span>
        );

      // Can use for future purpose.
      // case LOGS_ACTION.VIEWED:
      //   return (
      //     <span>
      //       {recordData?.[0]?.keyName} key viewed in{' '}
      //       {recordData?.[0]?.environment} environment.
      //     </span>
      //   );

      default:
        return <span> - </span>;
    }
  };

  return (
    <div className="table-card-page">
      <Card
        title={
          <>
            <div className="d-flex gap-16 align-center">
              <Button
                type="text"
                shape="square"
                onClick={() => {
                  setProjectTopicId(null);
                  navigate(ROUTES.MAIN, {
                    state: {
                      projectData: state?.projectData,
                      topicsId: state?.topicsId,
                    },
                  });
                }}
                icon={<ArrowLeftOutlined />}
              />
              <h2 className="m-0">History</h2>
            </div>
          </>
        }
        className="ant-body-scroll"
      >
        <div className="d-flex flex-vertical gap-16">
          <div className="history-table">
            <Table
              bordered
              columns={columns}
              size="small"
              rowKey={(record) => record?.id}
              dataSource={data?.viewHistory?.data}
              onChange={onChange}
              loading={loading}
              pagination={{
                current: currentPage,
                total: data?.viewHistory?.count,
                pageSize,
              }}
              scroll={{ x: 'auto', y: 'calc(100vh - 240px)' }}
              expandable={{
                expandedRowRender: (record) => (
                  <Descriptions
                    bordered
                    size="small"
                    className="sub-table-container"
                  >
                    <Descriptions.Item span={3} label="Description">
                      {record?.log}
                    </Descriptions.Item>
                    {record?.action !== LOGS_ACTION?.EXPORTED && (
                      <>
                        <Descriptions.Item label="Old Data">
                          {getRecords(record?.action, record, 'oldData')}
                        </Descriptions.Item>
                        <Descriptions.Item label="New Data">
                          {getRecords(record?.action, record, 'newData')}
                        </Descriptions.Item>
                      </>
                    )}
                  </Descriptions>
                ),
                rowExpandable: (record) => record?.name !== 'Not Expandable',
              }}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}
